/**
 * Leave Request Edit Page — approve/reject/cancel for managers.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import {
  Button, Card, CardContent, Label, Textarea,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

async function callBLL(path: string, method = 'POST', body?: unknown) {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'cancelled'];

export function LeaveEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'leave_requests', id: id! });
  const [status, setStatus] = React.useState('pending');
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const rec = data?.data as Record<string, unknown> | undefined;

  React.useEffect(() => {
    if (rec) {
      setStatus(String(rec.status ?? 'pending'));
      setRejectionReason(String(rec.rejection_reason ?? ''));
    }
  }, [rec]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let result: { success?: boolean; error?: { message?: string } };
      if (status === 'approved') {
        result = await callBLL(`/api/v1/hros/leave/requests/${id}/approve`, 'POST');
      } else if (status === 'rejected') {
        result = await callBLL(`/api/v1/hros/leave/requests/${id}/reject`, 'POST', { reason: rejectionReason });
      } else if (status === 'cancelled') {
        result = await callBLL(`/api/v1/hros/leave/requests/${id}/cancel`, 'POST');
      } else {
        // update reason/dates via PUT
        result = await callBLL(`/api/v1/hros/leave/requests/${id}`, 'PUT', { reason: rejectionReason });
      }
      if (result?.success) {
        navigate(`/leave/${id}`);
      } else {
        setError(result?.error?.message ?? 'Update failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  }

  if (fetchLoading || !rec) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/leave/${id}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Update Leave Request</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 p-4 bg-muted rounded-lg text-sm space-y-1">
            <div><span className="font-medium">Start:</span> {String(rec.start_date ?? '').slice(0, 10)}</div>
            <div><span className="font-medium">End:</span> {String(rec.end_date ?? '').slice(0, 10)}</div>
            <div><span className="font-medium">Duration:</span> {String(rec.duration_days ?? '')} days</div>
            {rec.reason && <div><span className="font-medium">Reason:</span> {String(rec.reason)}</div>}
            <div><span className="font-medium">Current Status:</span> {String(rec.status ?? '')}</div>
          </div>

          {error && <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="status">Action / New Status</Label>
              <select
                id="status"
                className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            {status === 'rejected' && (
              <div className="space-y-1">
                <Label htmlFor="rejection_reason">Rejection Reason *</Label>
                <Textarea
                  id="rejection_reason"
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  rows={3}
                  required
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(`/leave/${id}`)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Status'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default LeaveEditPage;
